import { useState, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Fragment } from 'react'
import ButtonSp from '../ui/ButtonSp'
import CloseIcon from '@material-ui/icons/Close'
import { useTranslation } from 'next-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import getConfig from 'next/config'
import Axios from 'axios'
import SuccessModal from '../common/SuccessModal'

const { publicRuntimeConfig } = getConfig()

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.homeStyle.title,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(0),
  },
  button: {
    ...theme.homeStyle.button,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  subtitle: {
    ...theme.homeStyle.subtitle,
    marginBottom: theme.spacing(5),
  },
}))

const ContactDevModal = ({ open, setOpen }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { register, handleSubmit, errors, control } = useForm()
  const classes = useStyles()
  const { t } = useTranslation('for-developers')

  const [isSubmitting, setSubmitting] = useState(false)
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false)
  const [isErrorModalOpen, setErrorModalOpen] = useState(false)
  const recaptchaRef = useRef(null)

  const renderFieldRule = (fieldName) => {
    switch (fieldName) {
      case 'phone':
        return {
          required: true,
          pattern: /^0\d{8,9}$/,
        }
      case 'email':
        return {
          required: true,
          pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
        }
      default:
        return {
          required: true,
        }
    }
  }

  const renderTextField = (
    label,
    name,
    placeholder,
    big = false,
    errorText
  ) => (
    <Grid item xs={12} sm={big ? 12 : 6}>
      <TextField
        variant="outlined"
        label={label}
        fullWidth
        inputRef={register(renderFieldRule(name))}
        name={name}
        multiline={big}
        rows={big ? 3 : 1}
        placeholder={placeholder}
        error={errors[name]}
        helperText={errors[name] && errorText}
      />
    </Grid>
  )

  const renderSelectField = (label, name, errorText, choices) => (
    <Grid item xs={12} sm={6}>
      <FormControl variant="outlined" fullWidth margin="none">
        <InputLabel error={errors[name]}>{label}</InputLabel>
        <Controller
          as={
            <Select label={label} fullWidth native error={errors[name]}>
              <option value="" disabled />
              {choices.map(([choiceLabel, choiceValue]) => (
                <option value={choiceValue} key={choiceValue}>
                  {choiceLabel}
                </option>
              ))}
            </Select>
          }
          control={control}
          name={name}
          defaultValue=""
          rules={{
            required: true,
          }}
        />
        {errors[name] && <FormHelperText error>{errorText}</FormHelperText>}
      </FormControl>
    </Grid>
  )

  const onSubmit = handleSubmit(async (data) => {
    try {
      setSubmitting(true)
      const token = await recaptchaRef.current.executeAsync()
      const {
        data: { success: captchaSuccess },
      } = await Axios.post('/api/recaptcha/validate', { recaptchaToken: token })
      if (captchaSuccess) {
        const {
          data: { status },
        } = await Axios.post('/api/api-partner/contact-dev', {
          ...data,
        })
        if (status === 'success') setSuccessModalOpen(true)
        else setErrorModalOpen(true)
      } else {
        setErrorModalOpen(true)
      }
    } catch (err) {
      setErrorModalOpen(true)
    } finally {
      setSubmitting(false)
      recaptchaRef.current.reset()
    }
  })

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullScreen={isMobile}
      >
        <IconButton
          className={classes.closeButton}
          onClick={() => setOpen(false)}
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="form"
          onSubmit={onSubmit}
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          p={4}
        >
          <h1 className={classes.title}>{t('form.title2')}</h1>
          <h2 className={classes.subtitle}>{t('form.subtitle2')}</h2>
          <Grid container spacing={2}>
            {[
              [
                t('form.name-surname'),
                'name',
                t('form.name-surname-p'),
                t('form.error.name-required'),
              ],
              [
                t('form.phone'),
                'phone',
                t('form.phone-p'),
                t('form.error.phone'),
              ],
              [
                t('form.email'),
                'email',
                t('form.email-p'),
                t('form.error.email'),
              ],
            ].map(([label, name, placeholder, errorText]) => (
              <Fragment key={name}>
                {renderTextField(label, name, placeholder, false, errorText)}
              </Fragment>
            ))}
            {renderSelectField(
              t('form.problem-type'),
              'type',
              t('form.error.problem-type'),
              [
                [t('form.problem-type-choices.api'), 'api'],
                [t('form.problem-type-choices.plugin'), 'plugin'],
                [t('form.problem-type-choices.other'), 'other'],
              ]
            )}
            {renderTextField(
              t('form.detail'),
              'detail',
              t('form.detail-p'),
              true,
              t('form.error.detail')
            )}
          </Grid>
          <Box p={2} />
          <ButtonSp
            type="submit"
            className={classes.button}
            loading={isSubmitting}
          >
            {t('form.submit')}
          </ButtonSp>
        </Box>
        <ReCAPTCHA
          size="invisible"
          sitekey={publicRuntimeConfig.recaptcha2Key}
          ref={recaptchaRef}
          badge="bottomleft"
          style={{
            zIndex: 999,
          }}
        />
      </Dialog>
      <SuccessModal
        isOpen={isSuccessModalOpen}
        setOpen={setSuccessModalOpen}
        title={t('form.success-modal.title-contact')}
        subtitle={t('form.success-modal.subtitle-contact')}
      />
      <SuccessModal
        isOpen={isErrorModalOpen}
        setOpen={setErrorModalOpen}
        title={t('form.error-modal.title')}
        subtitle={t('form.error-modal.subtitle')}
        type="error"
      />
    </>
  )
}

export default ContactDevModal
